import React, {useMemo, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';

import {useAlert} from 'src/shared/hooks/useAlert';

import {useDisclosure} from '@mantine/hooks';
import {IconCertificate} from '@tabler/icons-react';
import type {
  ItemAssignmentRequest,
  ItemAssignmentResponse,
} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  ItemAssignmentStateEnum,
  ModuleEnum,
  NoteModuleEnum,
  NotificationModuleEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateItemAssignmentMutation,
  useDeleteItemAssignmentMutation,
  useGetItemAssignmentQuery,
  useUpdateItemAssignmentMutation,
} from 'src/redux/queries/itemAssignment';
import {useGetItemDefinitionQuery} from 'src/redux/queries/itemDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SplitPane} from 'src/design/atoms/SplitPane';
import {
  SidebarLinkItemTracker,
  SidebarLinkPersonnel,
} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useItemTrackerBreadcrumbs} from 'src/shared/hooks/useItemTrackerBreadcrumbs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotesModule} from 'src/app/notes/NotesModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {ViewLinkedWorkflowSection} from 'src/app/workflows/ViewLinkedWorkflowSection';
import {
  CreateDefaultItemAssignment,
  ItemAssignmentMeta,
  ItemAssignmentPillConfig,
} from './itemAssignmentHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type ItemAssignmentViewType =
  | 'content'
  | 'attachments'
  | 'notes'
  | 'notifications'
  | 'audit'
  | ''
  | ':view';

export interface ItemAssignmentFormProps {
  definitionId?: number;
  id?: number;
  mode: 'create' | 'view' | 'edit' | 'clone';
  enableEdit: boolean;
  enableTransitions: boolean;
  fromDashboard?: boolean;
}

export const ItemAssignmentForm: React.FC<ItemAssignmentFormProps> = ({
  definitionId,
  id,
  mode,
  enableEdit,
  enableTransitions,
  fromDashboard = false,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const itemAssignPerms = useModulePermissions(
    ModuleEnum.ItemTrackerAssignments
  );
  const personnelPerms = usePersonnelPermissions();
  const auditPerms = useAuditLogPermissions();

  const [view, setView] = useState<ItemAssignmentViewType>('content');
  const [machine, isMachineLoading] = useStateMachine({
    url: ApiEndpoints.getItemAssignmentStateMachine,
  });

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const {
    data: personnel,
    isFetching: isPersonnelFetching,
    isLoading: isPersonnelLoading,
  } = useGetPersonnelSelectOptionsQuery({
    start: 0,
    length: 10000,
    active: [UserStatusEnum.Active],
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    superviserOnly: false,
  });

  const formMeta = useMemo(
    () => ItemAssignmentMeta((personnel?.data ?? []) as ISelectOption[]),
    [personnel?.data]
  );

  const {data: definition, isLoading: isDefinitionLoading} =
    useGetItemDefinitionQuery(definitionId ?? 0, {
      skip: definitionId === undefined,
    });

  const {
    data: fetchedAssignment,
    isLoading: isAssignmentLoading,
    refetch: refetchAssignment,
  } = useGetItemAssignmentQuery(id ?? 0, {
    skip: id === undefined || mode === 'create',
  });

  const item: ItemAssignmentResponse | undefined = useMemo(() => {
    if (!fetchedAssignment) return undefined;
    if (mode === 'clone') {
      return {
        serial: fetchedAssignment.serial,
        description: fetchedAssignment.description,
        state: ItemAssignmentStateEnum.Draft,
        assignedToUserId: fetchedAssignment.assignedToUserId,
        itemDefinitionId: fetchedAssignment.itemDefinitionId,
      } as ItemAssignmentResponse;
    }
    return fetchedAssignment;
  }, [fetchedAssignment, mode]);

  const getBreadcrumbMode = () => {
    switch (mode) {
      case 'create':
        return 'assignment-create';
      case 'edit':
        return 'assignment-edit';
      case 'clone':
        return 'assignment-clone';
      default:
        return 'assignment-view';
    }
  };

  const {breadcrumbs, fallbackTo} = useItemTrackerBreadcrumbs({
    mode: getBreadcrumbMode(),
    definitionId: definitionId,
    assignmentId: id,
    itemDefinitionName: definition?.name || item?.itemDefinition?.name,
    assigneeName: item?.assignedToUser?.name,
    fromDashboard,
  });

  const form = useEntitySyncedForm({
    formMeta,
    initialData: CreateDefaultItemAssignment(),
    entity: item,
    toFormData: entity =>
      toFormDataV2({...CreateDefaultItemAssignment(), ...entity}),
  });

  const [isSavePending, setIsSavePending] = useState(false);
  const isLoading =
    isAssignmentLoading ||
    isDefinitionLoading ||
    isPersonnelFetching ||
    isPersonnelLoading ||
    isMachineLoading;

  const [triggerCreate] = useCreateItemAssignmentMutation();
  const [triggerUpdate] = useUpdateItemAssignmentMutation();
  const [triggerDelete] = useDeleteItemAssignmentMutation();

  const handleSave = async () => {
    try {
      setIsSavePending(true);
      const values = form.getFieldValues();
      const body: ItemAssignmentRequest = {
        id: mode === 'edit' ? id : undefined,
        assignedToUserId: values.assignedToUserId as number | undefined,
        itemDefinitionId: definitionId as number,
        serial: values.serial as string | undefined,
        description: values.description as string | undefined,
      };

      let res: ItemAssignmentResponse | undefined;
      switch (mode) {
        case 'clone':
          res = await triggerCreate(body).unwrap();
          break;
        case 'create':
          res = await triggerCreate(body).unwrap();
          break;
        case 'edit':
          res = await triggerUpdate(body).unwrap();
          break;

        default:
          throw new Error('Invalid mode for API call.');
      }

      setAlert('Item was successfully updated', 'success');

      navigate(
        PathUtility.ItemAssignmentView(String(definitionId), String(res!.id)),
        {replace: true}
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!id || !definitionId) {
        setAlert('ID not found.', 'error');
        return;
      }

      await triggerDelete(+id!).unwrap();

      setAlert(
        'Item was successfully deleted. This change may take a few minutes to appear.',
        'success'
      );

      navigate(PathUtility.ItemDefinitionView(definitionId!, 'content'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleEdit = () =>
    navigate(PathUtility.ItemAssignmentEdit(String(definitionId), String(id)));

  const handleClone = () =>
    navigate(PathUtility.ItemAssignmentClone(String(definitionId), String(id)));

  const handleCertificate = () =>
    navigate(
      PathUtility.ItemTrackerIssueCertificate(String(definitionId), String(id))
    );

  const handleGoToItem = () =>
    navigate(PathUtility.ItemDefinitionView(String(definitionId), 'content'));

  const handleGoToPersonnel = () =>
    item?.assignedToUserId &&
    navigate(PathUtility.PersonnelView(item.assignedToUserId, 'item'));

  const extensionTabs = useExtensionTabs(
    ModuleEnum.ItemTrackerAssignments,
    id ?? 0
  );

  let title = `New ${definition?.name ?? 'Item'} Assignment`;
  if (item?.itemDefinition?.name) {
    title = item?.itemDefinition?.name;
  } else if (item && definition?.name) {
    title = definition.name;
  }
  if (item?.assignedToUser?.name) {
    title += ` (${item?.assignedToUser?.name})`;
  }

  const {RenderedTabsComponent} = useUrlQueryTabV2<ItemAssignmentViewType>({
    view: (view ?? '') as ItemAssignmentViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <SplitPane
            pane={<ViewLinkedWorkflowSection itemAssignmentId={item?.id} />}>
            <>
              {mode !== 'view' ? (
                <>
                  <ProactForm form={form}>
                    <ProactFormGrid />
                  </ProactForm>
                  <SaveButton
                    loading={isSavePending}
                    onClick={handleValidateAndSave}
                  />
                </>
              ) : (
                <>
                  <FormViewComponentV2
                    formData={toFormDataV2(item)}
                    formMeta={formMeta}
                  />

                  {machine && item && enableTransitions === true && (
                    <FormTransitionBar
                      module={ModuleEnum.ItemTrackerAssignments}
                      stateMachine={machine}
                      stateId={item.state}
                      transitionBaseUrl={`${ApiEndpoints.transitionItemAssignment}/${id}/transition`}
                      onRefreshTransition={refetchAssignment}
                      invalidateTagsOnComplete={['ItemAssignments']}
                      fallbackTo={fallbackTo}
                    />
                  )}
                </>
              )}
            </>
          </SplitPane>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: mode !== 'view' || !itemAssignPerms.attachments,
        component: (
          <>
            {item?.id && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.ItemAssignment}
                entityId={item.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: mode !== 'view' || !itemAssignPerms.notes,
        component: (
          <>
            <NotesModule
              entityId={item?.id ?? 0}
              noteType={NoteModuleEnum.ItemAssignment}
            />
          </>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: mode !== 'view' || !itemAssignPerms.notifications,
        component: (
          <>
            <NotificationsModule
              entityId={item?.id ?? 0}
              notificationType={NotificationModuleEnum.ItemAssignment}
            />
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: mode !== 'view' || !auditPerms.view,
        component: item?.id && (
          <AuditModule
            entityId={String(item.id)}
            entityType={EntityIdentifierEnum.ItemAssignment}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view => setView(view as ItemAssignmentViewType),
  });

  const pills = ItemAssignmentPillConfig(item);
  const canEdit = mode === 'view' && enableEdit && itemAssignPerms.update;
  const canClone = mode === 'view' && itemAssignPerms.create;
  const canDelete = mode === 'view' && itemAssignPerms.delete;
  const hasCertificate = mode === 'view' && item?.hasIssueCertificate === true;

  return isLoading === false ? (
    <>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkItemTracker.icon}>
          {title}
        </PageHeader.Title>
        {item?.internalIdentifier && (
          <PageHeader.Meta>{item.internalIdentifier}</PageHeader.Meta>
        )}
        {pills.length > 0 && (
          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>
        )}
        <PageHeader.Actions>
          <PageHeader.Action
            onClick={handleGoToItem}
            icon={SidebarLinkItemTracker.icon}>
            Go to Item
          </PageHeader.Action>
          {personnelPerms.view &&
            mode !== 'create' &&
            item?.assignedToUserId && (
              <PageHeader.Action
                icon={SidebarLinkPersonnel.icon}
                onClick={handleGoToPersonnel}>
                Go to Personnel
              </PageHeader.Action>
            )}
          {canClone && (
            <PageHeader.Action variant="clone" onClick={handleClone}>
              Clone
            </PageHeader.Action>
          )}
          {hasCertificate && (
            <PageHeader.Action onClick={handleCertificate}>
              <IconCertificate size={16} style={{marginRight: 4}} />
              Certificate
            </PageHeader.Action>
          )}
          {canEdit && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}
          {canDelete && (
            <PageHeader.Action
              variant="delete"
              onClick={() => confirmDeleteHandlers.open()}>
              Delete
            </PageHeader.Action>
          )}
          {mode !== 'view' && (
            <PageHeader.Action
              variant="save"
              onClick={handleValidateAndSave}
              loading={isSavePending}>
              Save
            </PageHeader.Action>
          )}
        </PageHeader.Actions>
      </PageHeader>

      {RenderedTabsComponent}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this item assignment?"
        body="This action cannot be undone. All data relating to this record will also be deleted, including notes and notification history."
      />
    </>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
